import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  vendorPortalApi,
  type QuotationStatus,
  type VendorQuotation,
} from '../../features/vendorPortal/api.ts';
import { Icon } from '../../components/Icon.tsx';
import { t } from '../../lib/strings.ts';
import { formatDate } from '@/lib/format';
import { Pagination } from '../../components/Pagination.tsx';

const PAGE_SIZE = 10;

function statusPill(status: QuotationStatus): string {
  switch (status) {
    case 'won': return 'pill pill-success';
    case 'lost': return 'pill pill-danger';
    case 'submitted': return 'pill pill-active';
    case 'pending': return 'pill pill-pending';
  }
}

function statusLabel(status: QuotationStatus): string {
  switch (status) {
    case 'won': return t.vendorPortal.statusWon;
    case 'lost': return t.vendorPortal.statusLost;
    case 'submitted': return t.vendorPortal.statusSubmitted;
    case 'pending': return t.vendorPortal.statusPending;
  }
}

type StatusFilter = 'all' | QuotationStatus;

export function VendorQuotations(): JSX.Element {
  const { data: quotations = [], isLoading, isError } = useQuery({
    queryKey: ['vendor-quotations'],
    queryFn: () => vendorPortalApi.quotations(),
  });

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  // Title/ref search, debounced so we don't filter per keystroke.
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim().toLowerCase()), 300);
    return () => clearTimeout(id);
  }, [search]);

  // Page resets whenever a filter/search changes (the result set changes).
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [statusFilter, debouncedSearch]);

  const filtered = useMemo(() => quotations.filter((q) => {
    if (statusFilter !== 'all' && q.status !== statusFilter) return false;
    if (debouncedSearch) {
      const hay = `${q.title} ${q.refNumber}`.toLowerCase();
      if (!hay.includes(debouncedSearch)) return false;
    }
    return true;
  }), [quotations, statusFilter, debouncedSearch]);

  const total = filtered.length;
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const statusOptions: { key: StatusFilter; label: string }[] = [
    { key: 'all', label: t.vendorPortal.filterAllStatuses },
    { key: 'won', label: t.vendorPortal.statusWon },
    { key: 'lost', label: t.vendorPortal.statusLost },
    { key: 'submitted', label: t.vendorPortal.statusSubmitted },
    { key: 'pending', label: t.vendorPortal.statusPending },
  ];

  return (
    <div className="page space-y-6">
      <header>
        <h1 className="font-serif text-3xl tracking-tight">{t.vendorPortal.quotationsTitle}</h1>
      </header>

      {/* Toolbar — status filter on one side, search on the other. Mirrors the staff list pattern. */}
      <div className="rounded-3xl bg-[hsl(var(--chrome))] flex flex-wrap items-center justify-between gap-x-6 gap-y-3 p-3 sm:px-4">
        <div className="flex flex-wrap items-center gap-2">
          <select
            aria-label={t.vendorPortal.colStatus}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="select w-auto rounded-full py-1.5"
          >
            {statusOptions.map((opt) => (
              <option key={opt.key} value={opt.key}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className="relative w-full sm:w-72">
          <Icon name="search" size={18} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t.vendorPortal.searchPlaceholder}
            className="input rounded-full py-1.5 ps-9 pe-4"
          />
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground" role="status" aria-live="polite">{t.vendorPortal.loading}</p>
      ) : isError ? (
        <p className="card-paper p-6 text-center text-sm text-destructive">{t.vendorPortal.loadError}</p>
      ) : (
        <section className="rounded-3xl bg-[hsl(var(--chrome))] divide-y divide-black/[0.05] overflow-hidden">
          {pageItems.map((q: VendorQuotation) => (
            <Link
              key={q.requestId}
              to="/vendor/requests/$requestId"
              params={{ requestId: q.requestId }}
              className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 p-4 transition-colors hover:bg-[hsl(var(--surface-high))] sm:px-5"
            >
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-x-2 gap-y-1 font-medium leading-snug">
                  <span>{q.title || q.refNumber}</span>
                  <span className={`${statusPill(q.status)} shrink-0`}>{statusLabel(q.status)}</span>
                </p>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  {q.title && <span className="mono-meta num" dir="ltr">{q.refNumber}</span>}
                  <span className="inline-flex items-center gap-1.5">
                    <Icon name="calendar_month" size={15} className="shrink-0 text-foreground/40" />
                    {t.vendorPortal.submittedAt} <span className="num">{formatDate(q.submittedAt)}</span>
                  </span>
                </div>
              </div>
            </Link>
          ))}
          {total === 0 && (
            <p className="p-8 text-center text-sm text-muted-foreground">{t.vendorPortal.quotationsEmpty}</p>
          )}
        </section>
      )}

      <Pagination page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} />
    </div>
  );
}
